/**
 * Curated photography for the public marketing surfaces.
 *
 * PHOTOGRAPHY IS THE COLOUR SYSTEM of the "Golden Hour" direction — the page
 * itself stays typography-on-cream, and the warmth comes from the images. So
 * the set is small, deliberate, and picked for one look: golden/blue hour,
 * real places, no stock-posed people.
 *
 * LICENSE: every frame here is an Unsplash asset used under the Unsplash
 * License (free to use commercially, no permission needed, no attribution
 * required). `Photo` still exposes an optional `credit` slot for surfaces that
 * want to credit anyway, and the self-hosted frames carry a ready-made one.
 *
 * TWO KINDS OF FRAME live here:
 *
 *  · SELF-HOSTED. Licensed originals pulled through the Unsplash API (download
 *    event honoured), re-encoded into an AVIF/WebP/JPEG ladder by
 *    `scripts/build-marketing-photos.mjs`, and committed under
 *    `public/photos/marketing/`. These carry `srcSet`/`sources`/dimensions from
 *    the generated manifest, so the browser negotiates format and width against
 *    OUR origin — no third-party DNS + TLS handshake in front of the LCP.
 *  · HOTLINKED. The remaining `images.unsplash.com` frames, which let the CDN
 *    negotiate format via `auto=format` and get their srcSet derived by
 *    `unsplashSrcSet()`. A slot stays hotlinked until its licensed original has
 *    been ingested; `selfHosted()` below falls back on its own, so a partially
 *    generated manifest degrades instead of failing to build.
 *
 * PROVENANCE: every hotlinked id below has been checked twice — the URL returns
 * HTTP 200 from the Unsplash CDN, AND the frame itself was looked at before
 * being assigned a slot. A 200 only proves something is served, not that it's
 * the right picture, so adding a NEW id means doing both again. Self-hosted
 * frames record their photo id and Unsplash page in the manifest instead.
 */

import {
  MARKETING_PHOTO_ASSETS,
  type MarketingPhotoAsset,
  type MarketingPhotoSource,
} from './marketingPhotoManifest'

const UNSPLASH_BASE = 'https://images.unsplash.com'

/**
 * Build a hotlinked marketing photo URL. `auto=format` lets the CDN negotiate
 * WebP/AVIF; `unsplashSrcSet()` (via LazyImage) rewrites `w=` across a width
 * ladder, so this width is only the fallback `src`.
 *
 * Keep `width` inside that ladder (it tops out at 1600 — see
 * `UNSPLASH_SRCSET_WIDTHS` in `lib/coverImages.ts`). A base wider than the
 * ladder is a rung the browser can never choose: it either downloads the
 * oversized fallback or silently caps at 1600 anyway, so the extra pixels only
 * ever cost bytes.
 */
function unsplash(photoId: string, width = 1600): string {
  return `${UNSPLASH_BASE}/${photoId}?w=${width}&q=80&auto=format&fit=crop`
}

export interface MarketingPhoto {
  /** Ready for `Photo`/`LazyImage` — a `/photos/**` path, or an Unsplash CDN url. */
  src: string
  /** i18n key holding the alt text. Alt copy is translatable, so it never lives here. */
  altKey: string
  /** Present on self-hosted frames only; hotlinked ones get a CDN-derived srcSet. */
  srcSet?: string
  /** AVIF/WebP alternatives, best-first. Self-hosted frames only. */
  sources?: readonly MarketingPhotoSource[]
  /** Intrinsic size of the widest rung — lets a caller reserve the box. */
  width?: number
  height?: number
  credit?: string
}

/**
 * Looked up by string rather than by key so an incomplete manifest (a frame
 * whose original hasn't been ingested yet) is a runtime fallback, not a
 * compile error. `MARKETING_PHOTO_ASSETS` is generated, so its key set moves.
 */
const ASSETS: Partial<Record<string, MarketingPhotoAsset>> = MARKETING_PHOTO_ASSETS

/**
 * A hotlinked frame to stand in while a self-hosted original is still missing.
 * It carries its OWN `altKey`: it is a different photograph, and alt text that
 * describes the picture we meant to ship rather than the one we did is worse
 * than no alt text at all.
 */
interface UnsplashFallback { photoId: string; altKey: string; width?: number }

/**
 * Alt copy is keyed to the PHOTOGRAPH, not to the slot — `landing.photos.frames.*`
 * mirrors the manifest slug — so a frame used in two slots is described once and
 * can never be described two different ways.
 */
function frameAltKey(slug: string): string {
  const camel = slug.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())
  return `landing.photos.frames.${camel}`
}

/** The self-hosted ladder for `slug`, or null while its original is missing. */
function selfHosted(slug: string): MarketingPhoto | null {
  const asset = ASSETS[slug]
  if (!asset) return null
  return {
    src: asset.src,
    altKey: frameAltKey(slug),
    srcSet: asset.srcSet,
    sources: asset.sources,
    width: asset.width,
    height: asset.height,
    credit: asset.credit,
  }
}

/** The hotlinked stand-in for a slot whose frame hasn't been ingested. */
function hotlinked(fallback: UnsplashFallback): MarketingPhoto {
  return { src: unsplash(fallback.photoId, fallback.width), altKey: fallback.altKey }
}


/* ── THE FRAME PLAN ───────────────────────────────────────────────────────────
 *
 * One config map, and the only place a photograph is assigned to a slot on the
 * page. Everything below it is derivation; nothing above it names a slot.
 *
 * Swapping any frame on the landing page is therefore three steps, none of them
 * in a component — see `scripts/build-marketing-photos.README.md`:
 *
 *   1. drop `<slug>.jpg` into the ingest directory (+ its provenance record),
 *   2. add `<slug>` to `SLOTS` in `scripts/build-marketing-photos.mjs` and point
 *      the plan entry below at it,
 *   3. re-run the script — it writes the ladder and the manifest.
 *
 * `frame` is a manifest slug and always wins. `fallback` is the hotlinked
 * Unsplash frame to show while that slug has no ingested original — so a plan
 * can name a frame that doesn't exist yet and the page still renders. A slot
 * with `frame: null` is one we have deliberately NOT self-hosted.
 */

interface SlotPlan {
  /** Manifest slug to serve from our own origin, or null to stay hotlinked. */
  frame: string | null
  /**
   * Another MANIFEST SLUG to stand in while `frame` has no ingested original.
   * Preferred over `fallback` wherever a plausible frame already exists in the
   * set: a stand-in from our own origin keeps the ladder, the preload, and the
   * measured contrast that a hotlinked CDN frame throws away.
   */
  standIn?: string
  /** Hotlinked stand-in. `null` means the slot simply disappears without a frame. */
  fallback: UnsplashFallback | null
}

/**
 * The base hero — the frame that ships in the prerendered HTML and carries the
 * LCP preload.
 *
 * Deliberately its OWN slug rather than "whichever city the first chip points
 * at": the base hero is the page's one art-directed conversion image and is
 * expected to become a hand-picked frame that is not a chip city at all. When
 * that lands, only `frame` below changes — the chip rotation excludes whatever
 * slug is the base, so all five chips simply become alternates.
 *
 * Austin is the approved base frame. It is the strongest frame the curated set
 * has for the job (a blue-hour skyline measuring 4.48:1 behind the headline),
 * and it ships as the page's self-hosted, preloaded LCP.
 */
const HERO_BASE: SlotPlan = {
  frame: 'hero-austin',
  // Red-and-gold lanterns over a Hoi An night market: dark across its upper
  // two-thirds with the highlights low-centre, so it holds `text-onmedia` copy
  // under the `strong` scrim. The original hero, kept as the stand-in.
  fallback: { photoId: 'photo-1526139334526-f591a54b477c', altKey: 'landing.photos.hero' },
}

/**
 * Hero frames keyed to the destination chips under the hero form.
 *
 * The hero used to show one fixed city while the chips offered five others,
 * which quietly told the visitor the pictures were decoration. Hovering (or
 * focusing) a chip now crossfades the hero to that city, so the photograph is
 * answering the chip.
 *
 * Keys mirror `HERO_IDEA_CHIPS` in `pages/Landing.tsx`. Every fallback is null
 * on purpose: a chip whose frame is missing just doesn't move the hero, which
 * is a better degradation than swapping in a photo of somewhere else.
 */
const HERO_CHIP_PLAN: Record<string, SlotPlan> = {
  lisbon: { frame: 'hero-lisbon', fallback: null },
  austin: { frame: 'hero-austin', fallback: null },
  sanDiego: { frame: 'hero-san-diego', fallback: null },
  tokyo: { frame: 'hero-tokyo', fallback: null },
  barcelona: { frame: 'hero-barcelona', fallback: null },
}

/**
 * Every other photographic slot on the page.
 *
 * The four `card*` slots are the Discover shelf — the product-output demo row.
 * Their CARD COPY (`landing.discoverSection.*Card`) has to describe the place
 * in the frame: these render as one card, so a picture that doesn't match its
 * headline reads as stock filler and undercuts the "this is what Discover
 * actually returns" claim. Each frame below was picked to match its card's
 * copy (a jazz-bar sign for the live-music card, an empty caldera-view terrace
 * for the cliffside taverna, a sunlit market stall for the Saturday market, a
 * riverside path for the slow Sunday walk) — none of them a headline surface,
 * so `SCRIM_MIN_CONTRAST` doesn't apply to them (see the manifest test).
 */
const SLOT_PLAN: Record<string, SlotPlan> = {
  /** Discover dusk panel. Texture behind the dusk ground at 30% — present as colour, not content. */
  discover: {
    frame: 'hero-tokyo',
    fallback: { photoId: 'photo-1493976040374-85c8e12f0c0e', altKey: 'landing.photos.discover' },
  },
  /**
   * Final-CTA dusk panel. The crew thesis frame: the closing ask is "bring your
   * group", so the page ends on a group. (The earlier aeroplane wing closed on
   * logistics.) It is a dusk GROUND at 22%, so it is texture rather than a
   * people appearance — see the note on PEOPLE_FRAMES.
   */
  finalCta: {
    frame: 'use-case-friends',
    fallback: { photoId: 'photo-1511632765486-a01980e01a18', altKey: 'landing.photos.finalCta' },
  },
  /** The Blue Note's own neon — a jazz-bar sign at night, matching the "live tonight" card copy. */
  cardEvent: {
    frame: 'card-event',
    fallback: null,
  },
  /** An empty terrace table over the caldera — the shelf's Greek cliffside taverna. */
  cardFood: {
    frame: 'card-food',
    fallback: null,
  },
  /** A sunlit farmers-market stall table, produce in baskets — the Saturday-morning market card. */
  cardActivity: {
    frame: 'card-activity',
    fallback: null,
  },
  /** A tree-lined riverside path in golden morning light — the slow Sunday walk. */
  cardWeekend: {
    frame: 'card-weekend',
    fallback: null,
  },
}

/**
 * The audience frames for `/use-cases/:slug` — one hero, one dusk ground under
 * the closing CTA, per page.
 *
 * EVERY AUDIENCE FRAME HAS PEOPLE IN IT, which is the point: a page addressed
 * to families opens on a family. That inverts the landing page's arrangement
 * rather than copying it — the landing hero is a place and its closing ask
 * ("bring your group") is the crew, while here the crew IS the premise, so
 * these pages open on the people and close on a destination. People still
 * appear exactly once per page; see `PEOPLE_FRAMES` and the test beside this
 * module.
 *
 * EVERY HERO CLEARS `SCRIM_MIN_CONTRAST` on its own measurement, so no page
 * borrows today. The `standIn` on each slot is the documented escape hatch for
 * when one does not: it takes another SELF-HOSTED frame rather than a hotlinked
 * Unsplash one, so the slot keeps a real ladder, a real preload, and a measured
 * contrast. The first `use-case-family-reunion` pick took that path at 2.92:1
 * before it was re-curated — the scrim is fixed on purpose, so a frame that
 * cannot hold the headline is the thing that gives way, not the floor.
 */
const USE_CASE_PLAN: Record<string, { hero: SlotPlan; cta: SlotPlan }> = {
  families: {
    hero: { frame: 'use-case-families', standIn: 'hero-san-diego', fallback: null },
    cta: { frame: 'hero-lisbon', fallback: null },
  },
  friends: {
    hero: { frame: 'use-case-friends', fallback: null },
    cta: { frame: 'hero-austin', fallback: null },
  },
  couples: {
    hero: { frame: 'use-case-couples', standIn: 'hero-lisbon', fallback: null },
    cta: { frame: 'hero-barcelona', fallback: null },
  },
  bachelorette: {
    hero: { frame: 'use-case-bachelorette', standIn: 'hero-barcelona', fallback: null },
    cta: { frame: 'hero-san-diego', fallback: null },
  },
  'family-reunion': {
    hero: { frame: 'use-case-family-reunion', standIn: 'hero-austin', fallback: null },
    // Not `hero-tokyo`, the other city frame with people in it: the hero here
    // already spends this page's one people frame.
    cta: { frame: 'hero-san-diego', fallback: null },
  },
}

/**
 * Manifest slugs whose frame contains recognisable people. Maintained by hand
 * against the frames' alt copy (`landing.photos.frames.*`) — add a slug here
 * the moment an ingested frame has people in it, or the once-per-page rule
 * silently stops being enforced.
 *
 * THE RULE COUNTS FRAMES SHOWN AS CONTENT. A `DuskPanel` ground is sunk to
 * ~22–30% behind the dusk gradient, which the panel's own contract calls
 * "present as colour and texture, never as something you're meant to read" —
 * you cannot tell whether anyone is in it. So a texture slot does not spend the
 * page's one appearance, and `LANDING_CONTENT_SLUGS` below records which
 * landing slots are content for the test that enforces this.
 */
export const PEOPLE_FRAMES: ReadonlySet<string> = new Set([
  'use-case-friends',
  'use-case-families',
  'use-case-couples',
  'use-case-bachelorette',
  'use-case-family-reunion',
  'hero-tokyo',
])

/**
 * Which landing slots present their frame AS CONTENT, and which sink it into a
 * dusk ground as texture. Exported for the once-per-page test — the rule is
 * about what a reader can actually see a person in, so it has to know the
 * difference. See PEOPLE_FRAMES.
 */
export const LANDING_CONTENT_SLUGS: Record<string, { slug: string | null; content: boolean }> = {
  hero: { slug: HERO_BASE.frame, content: true },
  discover: { slug: SLOT_PLAN.discover.frame, content: false },
  finalCta: { slug: SLOT_PLAN.finalCta.frame, content: false },
  cardEvent: { slug: SLOT_PLAN.cardEvent.frame, content: true },
  cardFood: { slug: SLOT_PLAN.cardFood.frame, content: true },
  cardActivity: { slug: SLOT_PLAN.cardActivity.frame, content: true },
  cardWeekend: { slug: SLOT_PLAN.cardWeekend.frame, content: true },
}

/** The dusk texture behind the compare pages' closing CTA. No people, no glamour. */
const COMPARE_CTA: SlotPlan = { frame: 'hero-austin', fallback: null }

// ── Derivation ───────────────────────────────────────────────────────────────

/**
 * The last resort: a photo with no photograph.
 *
 * `Photo` renders no `<img>` for an empty `src` and shows its warm ground
 * alone, which is a complete brand surface rather than a broken image. It
 * carries no `altKey` of its own precisely BECAUSE there is nothing to
 * describe — describing a picture that is not there is worse than saying
 * nothing, and `Photo` treats the empty ground as decorative.
 */
const NO_PHOTO: MarketingPhoto = { src: '', altKey: 'landing.photos.none' }

/**
 * Resolve one planned slot, in order: the self-hosted frame, its self-hosted
 * stand-in, its hotlinked stand-in, then nothing.
 *
 * THIS MUST NOT THROW. Every slot is resolved at module evaluation, and the kit
 * is imported by every marketing page — so a plan naming a frame the manifest
 * does not have (a fresh clone before `build-marketing-photos.mjs` has run, a
 * half-generated manifest, a typo'd slug) would white-screen the entire public
 * surface. The header of this file promises a partial manifest "degrades
 * instead of failing to build"; this is where that promise is kept.
 */
function resolve(plan: SlotPlan): MarketingPhoto {
  const frame = plan.frame ? selfHosted(plan.frame) : null
  if (frame) return frame
  const standIn = plan.standIn ? selfHosted(plan.standIn) : null
  if (standIn) return standIn
  if (plan.fallback) return hotlinked(plan.fallback)

  // Loud in dev and in the build log, silent for the visitor, who still gets a
  // warm gradient where the photograph should be.
  if (import.meta.env?.DEV) {
    console.warn(
      `[marketing frames] no frame for slot planned as "${plan.frame ?? plan.standIn ?? '(unnamed)'}" — `
      + 'rendering the warm ground. Run `node scripts/build-marketing-photos.mjs --src <dir>`.',
    )
  }
  return NO_PHOTO
}

/**
 * The chip frames the manifest actually has, minus whichever one is already the
 * base hero. Excluded BY SLUG, not by chip key: once the base becomes a frame
 * that is not a chip city, all five chips become alternates on their own, with
 * no code change here.
 */
export const HERO_CHIP_PHOTOS: Partial<Record<string, MarketingPhoto>> = (() => {
  const frames: Partial<Record<string, MarketingPhoto>> = {}
  for (const [chip, plan] of Object.entries(HERO_CHIP_PLAN)) {
    if (!plan.frame || plan.frame === HERO_BASE.frame) continue
    const photo = selfHosted(plan.frame)
    if (photo) frames[chip] = photo
  }
  return frames
})()

/**
 * The marketing photo set, keyed by the slot it fills. Keep this short — a page
 * that uses eight photographs has no photograph.
 */
export const MARKETING_PHOTOS = {
  /** Hero, full-bleed. The page's LCP; see `HERO_BASE`. */
  hero: resolve(HERO_BASE),
  discover: resolve(SLOT_PLAN.discover),
  finalCta: resolve(SLOT_PLAN.finalCta),
  cardEvent: resolve(SLOT_PLAN.cardEvent),
  cardFood: resolve(SLOT_PLAN.cardFood),
  cardActivity: resolve(SLOT_PLAN.cardActivity),
  cardWeekend: resolve(SLOT_PLAN.cardWeekend),
} satisfies Record<string, MarketingPhoto>

export type MarketingPhotoKey = keyof typeof MARKETING_PHOTOS

/** Which manifest slug a resolved slot actually ended up serving. */
function resolvedSlug(plan: SlotPlan): string | null {
  if (plan.frame && selfHosted(plan.frame)) return plan.frame
  if (plan.standIn && selfHosted(plan.standIn)) return plan.standIn
  return null
}

export interface UseCasePhotos {
  hero: MarketingPhoto
  cta: MarketingPhoto
  /** The slugs actually serving, for the once-per-page people assertion. */
  heroSlug: string | null
  ctaSlug: string | null
}

/** Hero + closing-CTA frames per use-case slug. See `USE_CASE_PLAN`. */
export const USE_CASE_PHOTOS: Record<string, UseCasePhotos> = Object.fromEntries(
  Object.entries(USE_CASE_PLAN).map(([slug, plan]) => [
    slug,
    {
      hero: resolve(plan.hero),
      cta: resolve(plan.cta),
      heroSlug: resolvedSlug(plan.hero),
      ctaSlug: resolvedSlug(plan.cta),
    },
  ]),
)

/** Dusk ground for the compare pages' closing CTA. */
export const COMPARE_CTA_PHOTO: MarketingPhoto = resolve(COMPARE_CTA)

/** True when a frame is served from our own origin rather than the Unsplash CDN. */
export function isSelfHosted(photo: MarketingPhoto): boolean {
  return photo.src.startsWith('/photos/')
}
